import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

interface TextInputDialogProps {
  open: boolean;
  title: string;
  label: string;
  description?: string;
  initialValue?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string | null;
  maxLength?: number;
  confirmLabel: string;
  cancelLabel?: string;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: (value: string) => Promise<void>;
}

export default function TextInputDialog({
  open,
  title,
  label,
  description,
  initialValue = '',
  placeholder,
  helperText,
  errorMessage,
  maxLength,
  confirmLabel,
  cancelLabel = 'Cancel',
  isPending = false,
  onCancel,
  onConfirm,
}: Readonly<TextInputDialogProps>): JSX.Element | null {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedValue = value.trim();
    if (!trimmedValue || (maxLength !== undefined && trimmedValue.length > maxLength)) {
      return;
    }

    void onConfirm(trimmedValue);
  };

  const trimmedValue = value.trim();
  const isTooLong = maxLength !== undefined && trimmedValue.length > maxLength;
  const validationMessage = isTooLong
    ? `${label} must be ${maxLength} characters or less.`
    : errorMessage;

  return (
    <Dialog open>
      <DialogOverlay onClick={onCancel} />
      <DialogContent role="dialog" aria-modal="true" aria-label={title}>
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {description ? (
            <p className="m-0 text-sm text-muted-foreground">{description}</p>
          ) : null}
          <div className="flex flex-col gap-[0.35rem]">
            <Label htmlFor="text-input-dialog-field">{label}</Label>
            <Input
              id="text-input-dialog-field"
              value={value}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={isPending}
              onChange={(event) => setValue(event.target.value)}
              autoFocus
            />
            {helperText ? (
              <p className="m-0 text-xs text-muted-foreground">{helperText}</p>
            ) : null}
            {validationMessage ? (
              <p className="m-0 text-xs text-destructive">{validationMessage}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
              {cancelLabel}
            </Button>
            <Button type="submit" disabled={!trimmedValue || isTooLong || isPending}>
              {isPending ? `${confirmLabel}...` : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
